import React from "react";
import {
  LayoutDashboard, Users, CalendarOff, Inbox, Wallet, PartyPopper, Megaphone,
  CalendarDays, Plus, Sparkles,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { Input, Field } from "../../components/forms";
import { StatCard } from "../../components/StatCard";
import { EmptyState } from "../../components/DataTable";
import { Drawer } from "../../components/Drawer";
import { Grid, Stack, Row, AiPanel, AiLines, MiniMonth, Legend } from "./kit";
import { useHrms, useFetch, fmtDate, fmtDateShort, fullName, monthKey } from "./useHrms";

// HRMS ▸ Overview — the daily HR screen, fully live: snapshot numbers,
// month calendar and feed all come from /api/v1/hrms/overview/*.

const TABS = [
  { key: "snapshot", label: "Snapshot", icon: <LayoutDashboard size={14} /> },
  { key: "calendar", label: "Calendar", icon: <CalendarDays size={14} /> },
  { key: "feed", label: "Feed", icon: <Megaphone size={14} /> },
];

export default function HrmsOverview() {
  const t = useDashTheme();
  const { isHr, post } = useHrms();
  const [tab, setTab] = React.useState("snapshot");
  const [announceOpen, setAnnounceOpen] = React.useState(false);
  const [annTitle, setAnnTitle] = React.useState("");
  const [annBody, setAnnBody] = React.useState("");
  const feed = useFetch("/overview/feed");

  const publish = async () => {
    if (!annTitle.trim()) return;
    await post("/announcements", { title: annTitle.trim(), body: annBody.trim() || null });
    setAnnounceOpen(false);
    setAnnTitle("");
    setAnnBody("");
    feed.refresh();
  };

  return (
    <PageShell
      eyebrow="HRMS"
      title="Overview"
      subtitle="The daily HR screen — what changed, what is blocked, what is coming"
      icon={<LayoutDashboard size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
      topRight={isHr && (
        <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={() => setAnnounceOpen(true)}>
          Announcement
        </Button>
      )}
    >
      {tab === "snapshot" && <Snapshot t={t} />}
      {tab === "calendar" && <CalendarTab t={t} />}
      {tab === "feed" && <FeedTab t={t} feed={feed} />}

      <Drawer open={announceOpen} onClose={() => setAnnounceOpen(false)} title="New announcement" width={420}>
        <Stack gap={t.s[4]}>
          <Field label="Title"><Input value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} placeholder="Townhall on Friday" /></Field>
          <Field label="Message"><Input value={annBody} onChange={(e) => setAnnBody(e.target.value)} placeholder="Details (optional)" /></Field>
          <Row gap={8}>
            <Button variant="primary" onClick={publish}>Publish</Button>
            <Button variant="ghost" onClick={() => setAnnounceOpen(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </PageShell>
  );
}

function Snapshot({ t }) {
  const { isHr, post } = useHrms();
  const { data, loading } = useFetch("/overview/snapshot");
  const [digest, setDigest] = React.useState(null);

  const snap = data || {};
  const att = snap.attendanceToday || {};
  const present = (att.present || 0) + (att.working || 0) + (att.half_day || 0);

  const runDigest = async () => {
    const d = await post("/ai/digest", {});
    setDigest(d);
    return null;
  };

  const digestLines = digest && (
    <AiLines items={[
      { text: `${digest.facts.pendingApprovals} approvals pending${digest.facts.oldestPendingApprovalDays ? `, oldest waiting ${digest.facts.oldestPendingApprovalDays} day(s)` : ""}.`, evidence: "Approvals" },
      { text: `${digest.facts.openExceptions} attendance exceptions are open.`, evidence: "Attendance ▸ Validation" },
      { text: `This month's payroll run is ${String(digest.facts.payrollRunStatus).replace("_", " ")}${digest.facts.payrollAnomalies.length ? `; ${digest.facts.payrollAnomalies.length} net-pay anomalies vs last cycle` : ""}.`, evidence: "Payroll" },
      ...(digest.facts.probationEndingSoon ? [{ text: `${digest.facts.probationEndingSoon} probation decision(s) due within 14 days.`, evidence: "Lifecycle" }] : []),
      ...(digest.narrative ? [{ text: digest.narrative }] : []),
    ]} />
  );

  return (
    <Stack gap={t.s[4]}>
      <Grid min={220}>
        {/* Headcount is an org-wide management number, so the API only sends it
            to HR. Everyone else gets the same cards scoped to themselves and,
            for managers, their reportees. */}
        {snap.headcount && (
          <StatCard
            label="Headcount" value={loading ? "…" : snap.headcount?.total ?? 0} icon={<Users size={12} />}
            sublabel={`${snap.headcount?.probation || 0} on probation · ${snap.headcount?.notice_period || 0} on notice`}
          />
        )}
        <StatCard
          label="On leave today"
          value={loading ? "…" : (snap.onLeaveToday || []).length} icon={<CalendarOff size={12} />}
          sublabel={`${present} tracked present today`} accent={t.status.warning}
        />
        <StatCard
          label={snap.scope === "org" ? "Pending approvals" : "Awaiting your decision"}
          value={loading ? "…" : snap.pendingApprovals ?? 0} icon={<Inbox size={12} />}
          sublabel={`${snap.openExceptions || 0} attendance exceptions open`} accent={t.status.info}
        />
        <StatCard
          label="Open tickets" value={loading ? "…" : snap.openTickets ?? 0} icon={<Wallet size={12} />}
          sublabel={snap.scope === "org" ? "help desk queue" : "raised by you"} accent={t.status.negative}
        />
      </Grid>

      {isHr && (
        <AiPanel title="Daily HR brief" cadence="ondemand" run={runDigest} actions={<Badge tone="neutral">Grounded in live records</Badge>}>
          {digestLines || <Row gap={8}><Sparkles size={13} /><span style={{ fontSize: t.font.size.base, color: t.c.textSecondary }}>Generate to summarise approvals, exceptions and payroll state.</span></Row>}
        </AiPanel>
      )}

      <Grid min={340}>
        <SectionCard title="On leave today" icon={<CalendarOff size={13} />} padding={0}>
          {(snap.onLeaveToday || []).length === 0 ? (
            <EmptyState title="Everyone is in" description="No approved leave for today." />
          ) : (
            <div>
              {snap.onLeaveToday.map((p, i) => (
                <Row key={p._id} justify="space-between" style={{
                  padding: `${t.s[3]}px ${t.s[4]}px`,
                  borderBottom: i === snap.onLeaveToday.length - 1 ? "none" : `1px solid ${t.c.border}`,
                }}>
                  <Row gap={10}>
                    <Avatar name={fullName(p.employeeId)} src={p.employeeId?.avatar} size={26} />
                    <div>
                      <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{fullName(p.employeeId)}</div>
                      <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{p.leaveTypeId?.name}</div>
                    </div>
                  </Row>
                  <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>till {fmtDateShort(p.toDate)}</span>
                </Row>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Celebrations this month" icon={<PartyPopper size={13} />} padding={0}>
          {(snap.celebrations || []).length === 0 ? (
            <EmptyState title="Nothing this month" description="Birthdays and anniversaries show here." />
          ) : (
            <div>
              {snap.celebrations.map((c, i) => (
                <Row key={`${c.userId}-${c.kind}`} justify="space-between" style={{
                  padding: `${t.s[3]}px ${t.s[4]}px`,
                  borderBottom: i === snap.celebrations.length - 1 ? "none" : `1px solid ${t.c.border}`,
                }}>
                  <Row gap={10}>
                    <Avatar name={c.name} src={c.avatar} size={26} />
                    <div>
                      <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{c.name}</div>
                      <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
                        {c.kind === "birthday" ? "Birthday" : `${c.years} year anniversary`}
                      </div>
                    </div>
                  </Row>
                  <Badge tone="neutral">{fmtDateShort(c.date)}</Badge>
                </Row>
              ))}
            </div>
          )}
        </SectionCard>
      </Grid>
    </Stack>
  );
}

function CalendarTab({ t }) {
  const now = new Date();
  const [month, setMonth] = React.useState(monthKey(now));
  const { data } = useFetch(`/overview/calendar?month=${month}`);
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1;

  const marks = {};
  for (const h of data?.holidays || []) {
    marks[new Date(h.date).toISOString().slice(0, 10)] = { tone: "holiday", label: h.name };
  }
  for (const l of data?.leaves || []) {
    for (let d = new Date(l.fromDate); d <= new Date(l.toDate); d = new Date(d.getTime() + 86400000)) {
      const k = d.toISOString().slice(0, 10);
      if (k.startsWith(month) && !marks[k]) marks[k] = { tone: "leave", label: `${fullName(l.employeeId)} on leave` };
    }
  }

  const shift = (delta) => {
    const d = new Date(Date.UTC(y, m + delta, 1));
    setMonth(d.toISOString().slice(0, 7));
  };

  const upcoming = [
    ...(data?.holidays || []).map((h) => ({ when: fmtDateShort(h.date), what: h.name, who: "Company holiday", at: h.date })),
    ...(data?.leaves || []).map((l) => ({
      when: `${fmtDateShort(l.fromDate)}–${fmtDateShort(l.toDate)}`,
      what: `${fullName(l.employeeId)} on ${l.leaveTypeId?.name || "leave"}`,
      who: `${l.days} day(s)`, at: l.fromDate,
    })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <Grid min={320}>
      <SectionCard
        title={new Date(y, m).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        subtitle="Holidays and approved leave in one view"
        actions={<Row gap={4}>
          <Button size="sm" variant="ghost" onClick={() => shift(-1)}>‹</Button>
          <Button size="sm" variant="ghost" onClick={() => shift(1)}>›</Button>
        </Row>}
      >
        <MiniMonth year={y} month={m} marks={marks} />
        <div style={{ marginTop: t.s[4], paddingTop: t.s[3], borderTop: `1px solid ${t.c.border}` }}>
          <Legend items={[
            { label: "Holiday", color: t.status.info },
            { label: "On leave", color: t.status.warning },
          ]} />
        </div>
      </SectionCard>

      <SectionCard title="Coming up" padding={0}>
        {upcoming.length === 0 ? (
          <EmptyState title="Clear month" description="No holidays or approved leave this month." />
        ) : (
          <div>
            {upcoming.map((e, i) => (
              <Row key={`${e.when}-${e.what}`} justify="space-between" style={{
                padding: `${t.s[3]}px ${t.s[4]}px`,
                borderBottom: i === upcoming.length - 1 ? "none" : `1px solid ${t.c.border}`,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{e.what}</div>
                  <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{e.who}</div>
                </div>
                <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, whiteSpace: "nowrap" }}>{e.when}</span>
              </Row>
            ))}
          </div>
        )}
      </SectionCard>
    </Grid>
  );
}

function FeedTab({ t, feed }) {
  const items = feed.data || [];
  return (
    <Stack gap={t.s[4]}>
      {items.length === 0 && <EmptyState title="Quiet so far" description="Announcements and record changes show here." />}
      {items.map((item, i) => item.kind === "announcement" ? (
        <SectionCard
          key={`a-${i}`} title={item.data.title}
          subtitle={`${fullName(item.data.createdBy)} · ${fmtDate(item.at)}`}
          icon={<Megaphone size={13} />}
          actions={item.data.pinned ? <Badge tone="info">Pinned</Badge> : null}
        >
          {item.data.body && (
            <p style={{ margin: 0, fontSize: t.font.size.base, color: t.c.textSecondary, lineHeight: "20px" }}>{item.data.body}</p>
          )}
        </SectionCard>
      ) : (
        <Row key={`f-${i}`} justify="space-between" style={{
          padding: `${t.s[3]}px ${t.s[4]}px`, border: `1px solid ${t.c.border}`, borderRadius: t.r.md, background: t.c.surface,
        }}>
          <Row gap={10}>
            <Avatar name={fullName(item.data.employeeId)} src={item.data.employeeId?.avatar} size={26} />
            <div>
              <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{item.data.what}</div>
              <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
                {fullName(item.data.employeeId)}{item.data.changedBy ? ` · by ${fullName(item.data.changedBy)}` : ""}
              </div>
            </div>
          </Row>
          <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>{fmtDate(item.at)}</span>
        </Row>
      ))}
    </Stack>
  );
}
